import React from "react";
import { FaStar, FaWhatsapp } from "react-icons/fa";
import { GiWaveSurfer } from "react-icons/gi";
import {} from "react-router-dom";
import { speedBoatData } from "../data/dummyData";
import SEO from "../components/common/SEO";
import NavFilters from "../components/common/NavFilters";

const SpeedBoatRentals = () => {
  const heroImage = "images/speedboat/jetski.jpg";

  // Filter for Jet Ski Adventure only
  const rentals = speedBoatData.filter(
    (boat) => boat.slug === "jet-ski-adventure",
  );

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="Jet Ski Rental Durrës Albania | From €60 – SeaPulse"
        description="Rent a jet ski in Durrës, Albania from €60. 15, 20 or 30-minute sessions on the Adriatic Sea. No license needed, safety briefing included. Book online instantly with SeaPulse Albania."
        keywords="jet ski rental Durrës, jet ski Albania, jet ski hire Durrës, jet ski Adriatic Albania, watercraft rental Albania, jet ski adventure Durrës, sea scooter Albania, water sports rental Durrës, jet ski excursion Albania, rent jet ski Albania, book jet ski Durrës, Durrës marina jet ski, Albanian coast jet ski"
        canonical="/jet-ski"
      />
      {/* Hero Banner */}
      <div className="relative h-[450px] md:h-[500px] lg:h-[550px] flex items-center justify-center overflow-hidden">
        <img
          src={heroImage}
          alt="Boat Rentals"
          className="absolute inset-0 w-full h-full object-cover brightness-90 dark:brightness-75"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/70 via-cyan-800/60 to-blue-600/50" />
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 max-w-5xl mx-auto">
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-5 py-2 rounded-full mb-6 border border-white/30 flex items-center gap-2">
            <GiWaveSurfer className="text-xl" /> Boat Rentals
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white drop-shadow-2xl mb-6 leading-tight">
            Boat Rentals
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/95 max-w-3xl leading-relaxed drop-shadow-lg mb-8">
            Rent jet skis, speedboats and watercraft to explore the Albanian
            coast at your own pace
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 text-white/90 text-xs sm:text-sm">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span>⭐</span>
              <span className="font-semibold">5.0 Rating</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span>🛡️</span>
              <span className="font-semibold">Licensed & Insured</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span>🎖️</span>
              <span className="font-semibold">Self-Drive or Skipper</span>
            </div>
          </div>
        </div>
      </div>

      <NavFilters />

      {/* Rentals Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-4">
            Available Rentals
          </h2>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Choose from our fleet of modern watercraft and explore the Albanian
            Riviera
          </p>
        </div>

        <div className="flex flex-col gap-6">
          {rentals.map((boat) => {
            const {
              id,
              name,
              rating,
              image,
              number_of_reviews,
              price,
              description,
              time,
            } = boat;

            return (
              <div
                key={id}
                className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden hover:border-cyan-400 dark:hover:border-cyan-500 hover:-translate-y-1"
              >
                <div className="flex flex-col lg:flex-row">
                  {/* Image Section */}
                  <div className="relative w-full lg:w-80 h-64 lg:h-72 flex-shrink-0 overflow-hidden">
                    <img
                      src={image}
                      alt={name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-xl">
                      ⚡ Featured
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="flex-1 p-6 lg:p-8 flex flex-col">
                    <div className="flex-1">
                      <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors duration-300">
                        {name}
                      </h3>
                      <p className="text-base text-gray-600 dark:text-gray-300 mb-5 leading-relaxed line-clamp-3">
                        {description}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 mb-6">
                        <div className="flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2 rounded-lg">
                          <FaStar className="text-yellow-500" />
                          <span className="font-bold text-gray-900 dark:text-white">
                            {rating}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            ({number_of_reviews} reviews)
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <span className="text-lg">⏱️</span>
                          <span className="font-medium">{time}</span>
                        </div>
                      </div>
                    </div>

                    {/* Price & Action Section */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-5 border-t border-gray-200 dark:border-gray-700">
                      <div>
                        <span className="text-sm text-gray-500 dark:text-gray-400 block mb-1 font-medium">
                          Starting from
                        </span>
                        <div className="text-4xl font-extrabold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                          €{price}
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          per person
                        </span>
                      </div>
                      <div className="flex flex-col gap-2 w-full sm:w-auto">
                        <div className="flex items-center justify-center sm:justify-end gap-2 bg-cyan-50 border border-cyan-200 text-cyan-800 text-sm font-semibold px-4 py-2.5 rounded-xl">
                          <span>🎯</span> Available on Board — No Pre-Booking
                          Needed
                        </div>
                        <a
                          href="https://wa.me/355692311606?text=Hello%2C%20I'd%20like%20more%20info%20about%20the%20Jet%20Ski%20Adventure"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <button className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl text-base">
                            <FaWhatsapp className="text-xl" /> Ask on WhatsApp
                          </button>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SpeedBoatRentals;
